import { useState, useEffect } from 'react';
import {
    Box, Heading, Table, Thead, Tbody, Tr, Th, Td,
    Badge, Button, HStack, useToast, Spinner, Center,
    Card, CardBody, Modal, ModalOverlay, ModalContent,
    ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
    FormControl, FormLabel, Input, Select, useDisclosure,
    InputGroup, InputLeftElement, IconButton, Flex, Tooltip, TableContainer, Text
} from '@chakra-ui/react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import StaffFormModal from '../components/StaffFormModal';
import { FiPlus, FiTrash2, FiEdit, FiSearch, FiDownload, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const ROLE_MAP = {
    teacher: { label: '帶班老師', color: 'green' },
    receptionist: { label: '行政老師', color: 'blue' },
    admin: { label: '機構管理員', color: 'purple' },
    super_admin: { label: '超級管理員', color: 'red' },
    parent: { label: '家長', color: 'orange' }
};

const StaffPage = ({ viewMode = 'staff' }) => {
    const { user } = useAuth();
    const isParentView = viewMode === 'parent';
    const isAllView = viewMode === 'all'; // For Admin Dashboard
    const pageTitle = isParentView ? '家長管理' : '教職員管理';

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [phoneQuery, setPhoneQuery] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [appliedPhone, setAppliedPhone] = useState('');
    const [filterRole, setFilterRole] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const itemsPerPage = 40;

    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();

    // Data State
    const [users, setUsers] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editUser, setEditUser] = useState(null);

    // Export State
    const [exportStartDate, setExportStartDate] = useState('');
    const [exportEndDate, setExportEndDate] = useState('');
    const { isOpen: isExportOpen, onOpen: onExportOpen, onClose: onExportClose } = useDisclosure();

    // Reset Page on Filter Change
    useEffect(() => {
        setCurrentPage(1);
    }, [filterRole, viewMode]);

    const handleSearchSubmit = () => {
        setAppliedSearch(searchTerm.trim());
        setAppliedPhone(phoneQuery.trim());
        setCurrentPage(1);
    };

    const handleSearchKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSearchSubmit();
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const params = {
                limit: itemsPerPage,
                skip: (currentPage - 1) * itemsPerPage
            };

            // Search Params
            if (appliedSearch) params.search = appliedSearch;
            else if (appliedPhone) params.search = appliedPhone;

            // Role Params
            if (isParentView) {
                params.role = 'parent';
            } else if (isAllView) {
                if (filterRole) params.role = filterRole;
            } else {
                // Staff View: Exclude parents
                if (filterRole) params.role = filterRole;
                else params.role_exclude = 'parent';
            }

            const [usersRes, classesRes] = await Promise.all([
                authAPI.getUsers(params),
                authAPI.getClasses() // Always fetch classes for Modal
            ]);

            setUsers(usersRes.data);
            setClasses(classesRes.data);

            // Total Count
            const total = parseInt(usersRes.headers['x-total-count'] || '0', 10);
            setTotalCount(total);

            if (user?.role === 'super_admin') {
                const instRes = await authAPI.getInstitutions();
                setInstitutions(instRes.data);
            }
        } catch (error) {
            console.error(error);
            toast({ title: "無法取得資料", status: "error", isClosable: true });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user) fetchData();
    }, [user, currentPage, appliedSearch, appliedPhone, filterRole, viewMode]);


    const handleExport = async () => {
        if (!exportStartDate || !exportEndDate) {
            toast({ title: "請選擇日期範圍", status: "warning" });
            return;
        }
        try {
            const response = await authAPI.attendance.exportHistory({
                start_date: exportStartDate,
                end_date: exportEndDate
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `attendance_${exportStartDate}_${exportEndDate}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            onExportClose();
            toast({ title: "匯出成功", status: "success" });
        } catch (error) {
            toast({ title: "匯出失敗", description: "請稍後再試", status: "error" });
        }
    };

    const handleEdit = (targetUser) => { setEditUser(targetUser); onOpen(); };

    const handleDelete = async (id) => {
        if (!window.confirm("確定要刪除這位使用者嗎？")) return;

        // 🚀 Optimistic Update
        const previousUsers = [...users];
        setUsers(prev => prev.filter(u => u.id !== id));
        setTotalCount(prev => Math.max(0, prev - 1));

        try {
            await authAPI.deleteUser(id);
            toast({ title: "刪除成功", status: "success", duration: 2000 });
            // No need to fetch, we already updated locally
        } catch (error) {
            console.error("Delete failed:", error);
            setUsers(previousUsers); // Rollback
            setTotalCount(previousUsers.length);
            toast({ title: "刪除失敗", description: "請檢查網路連線或權限", status: "error" });
        }
    };

    const handleOpen = () => { setEditUser(null); onOpen(); };
    const handleClose = () => { setEditUser(null); onClose(); };

    const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;

    return (
        <Flex direction="column" h="100%" w="100%" minW="0" overflow="hidden">
            <Box flexShrink={0} mb={4}>
                <HStack justifyContent="space-between" mb={6}>
                    <Heading size="lg" color="brand.800">{pageTitle}</Heading>
                    <HStack spacing={2}>
                        {!isParentView && (
                            <Button leftIcon={<FiDownload />} variant="outline" onClick={onExportOpen}>
                                匯出出勤
                            </Button>
                        )}
                        <Button leftIcon={<FiPlus />} colorScheme="brand" onClick={handleOpen}>
                            新增{isParentView ? '家長' : '人員'}
                        </Button>
                    </HStack>
                </HStack>

                <HStack mb={4} spacing={4}>
                    <InputGroup maxW="300px">
                        <InputLeftElement pointerEvents="none"><FiSearch color="gray.300" /></InputLeftElement>
                        <Input
                            placeholder={isParentView ? "搜尋學生姓名..." : "搜尋姓名..."}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            bg="white"
                        />
                    </InputGroup>

                    <InputGroup maxW="300px">
                        <InputLeftElement pointerEvents="none"><FiSearch color="gray.300" /></InputLeftElement>
                        <Input
                            placeholder="搜尋手機號碼..."
                            value={phoneQuery}
                            onChange={(e) => setPhoneQuery(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            bg="white"
                        />
                    </InputGroup>

                    <Button leftIcon={<FiSearch />} colorScheme="brand" onClick={handleSearchSubmit}>
                        搜尋
                    </Button>

                    {!isParentView && (
                        <Select
                            placeholder="所有角色"
                            maxW="200px"
                            bg="white"
                            value={filterRole}
                            onChange={(e) => setFilterRole(e.target.value)}
                        >
                            {Object.entries(ROLE_MAP)
                                .filter(([key]) => isAllView ? true : key !== 'parent')
                                .map(([key, config]) => (
                                    <option key={key} value={key}>{config.label}</option>
                                ))}
                        </Select>
                    )}
                </HStack>
            </Box>

            <Card w="100%" minW="0" flex="1" overflow="hidden">
                <CardBody p={0} position="relative" display="flex" flexDirection="column" h="100%">
                    <Box flex="1" overflow="hidden" position="relative">
                        <Box
                            position="absolute"
                            top={0}
                            left={0}
                            right={0}
                            bottom={0}
                            overflow="auto"
                            sx={{ overscrollBehavior: 'none' }}
                        >
                            <Table variant="simple" minW="800px" whiteSpace="nowrap" bg="white">
                                <Thead bg="gray.50" position="sticky" top={0} zIndex={1} shadow="sm">
                                    <Tr>
                                        <Th whiteSpace="nowrap">{isParentView ? '學生姓名' : '姓名'}</Th>
                                        {isParentView && <Th whiteSpace="nowrap">稱謂</Th>}
                                        <Th whiteSpace="nowrap">角色</Th>
                                        <Th whiteSpace="nowrap">手機號碼</Th>
                                        <Th whiteSpace="nowrap">所屬機構</Th>
                                        <Th whiteSpace="nowrap">操作</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {loading ? (
                                        <Tr><Td colSpan={6}><Center py={10}><Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="brand.500" size="xl" /></Center></Td></Tr>
                                    ) : (
                                        <>
                                            {users.map((u) => {
                                                const getInstName = () => {
                                                    if (u.institution?.name) return u.institution.name;
                                                    if (u.children && u.children.length > 0) {
                                                        const child = u.children[0];
                                                        if (child.class?.institution?.name) return child.class.institution.name;
                                                        if (child.classes && child.classes.length > 0 && child.classes[0].institution?.name) return child.classes[0].institution.name;
                                                    }
                                                    return '-';
                                                };

                                                return (
                                                    <Tr key={u.id}>
                                                        <Td fontWeight="bold" whiteSpace="nowrap">
                                                            {isParentView
                                                                ? (u.children && u.children.length > 0
                                                                    ? u.children.map(c => c.full_name).join(', ')
                                                                    : '未綁定')
                                                                : u.full_name
                                                            }
                                                        </Td>
                                                        {isParentView && <Td whiteSpace="nowrap">{u.full_name}</Td>}
                                                        <Td whiteSpace="nowrap">
                                                            <Badge colorScheme={ROLE_MAP[u.role]?.color || 'gray'}>
                                                                {ROLE_MAP[u.role]?.label || u.role}
                                                            </Badge>
                                                        </Td>
                                                        <Td whiteSpace="nowrap">{u.phone_number}</Td>
                                                        <Td whiteSpace="nowrap">{getInstName()}</Td>
                                                        <Td whiteSpace="nowrap">
                                                            <HStack spacing={2}>
                                                                <Tooltip label="編輯人員" placement="top" hasArrow>
                                                                    <IconButton icon={<FiEdit />} size="sm" colorScheme="teal" onClick={() => handleEdit(u)} aria-label="編輯" />
                                                                </Tooltip>
                                                                <Tooltip label="刪除人員" placement="top" hasArrow>
                                                                    <IconButton icon={<FiTrash2 />} size="sm" colorScheme="red" variant="ghost" onClick={() => handleDelete(u.id)} aria-label="刪除" />
                                                                </Tooltip>
                                                            </HStack>
                                                        </Td>
                                                    </Tr>
                                                );
                                            })}
                                            {users.length === 0 && (
                                                <Tr><Td colSpan={6} textAlign="center" py={8} color="neutral.500">尚無資料</Td></Tr>
                                            )}
                                        </>
                                    )}
                                </Tbody>
                            </Table>
                        </Box>
                    </Box>

                    {/* Pagination Footer */}
                    {totalCount > 0 && (
                        <Flex p={4} justify="space-between" align="center" borderTop="1px solid" borderColor="gray.100">
                            <Text fontSize="sm" color="gray.600">
                                顯示第 {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalCount)} 筆，共 {totalCount} 筆
                            </Text>
                            <HStack spacing={2}>
                                <Button
                                    size="sm"
                                    leftIcon={<FiChevronLeft />}
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    isDisabled={currentPage === 1}
                                    variant="outline"
                                >
                                    上一頁
                                </Button>
                                <Button size="sm" isDisabled _disabled={{ color: 'black', cursor: 'default', bg: 'gray.50' }} variant="ghost">
                                    {currentPage} / {totalPages}
                                </Button>
                                <Button
                                    size="sm"
                                    rightIcon={<FiChevronRight />}
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    isDisabled={currentPage === totalPages}
                                    variant="outline"
                                >
                                    下一頁
                                </Button>
                            </HStack>
                        </Flex>
                    )}
                </CardBody>
            </Card>

            <StaffFormModal
                isOpen={isOpen}
                onClose={handleClose}
                onSuccess={fetchData}
                classes={classes}
                institutions={institutions}
                user={user}
                isParentView={isParentView}
                isAllView={isAllView}
                editData={editUser}
            />
            <Modal isOpen={isExportOpen} onClose={onExportClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>匯出出勤紀錄 (CSV)</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        <FormControl isRequired mb={4}>
                            <FormLabel>開始日期</FormLabel>
                            <Input type="date" value={exportStartDate} onChange={(e) => setExportStartDate(e.target.value)} />
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel>結束日期</FormLabel>
                            <Input type="date" value={exportEndDate} onChange={(e) => setExportEndDate(e.target.value)} />
                        </FormControl>
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onExportClose} mr={3}>取消</Button>
                        <Button colorScheme="green" leftIcon={<FiDownload />} onClick={handleExport}>下載報表</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Flex>
    );
};

export default StaffPage;
